import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {LayoutDashboard, Heart, MessageSquare, Video, Calendar, Music, BookOpen, Baby, Users, Mail, FileText, LogOut, Menu, X} from 'lucide-react'
import { lumi } from '../../lib/lumi'

interface AdminLayoutProps {
  children: React.ReactNode
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const user = lumi.auth.user

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/prayers', icon: Heart, label: 'Orações' },
    { path: '/admin/testemunhos', icon: MessageSquare, label: 'Testemunhos' },
    { path: '/admin/sermons', icon: Video, label: 'Sermões' },
    { path: '/admin/events', icon: Calendar, label: 'Eventos' },
    { path: '/admin/worship', icon: Music, label: 'Louvores' },
    { path: '/admin/bible-school', icon: BookOpen, label: 'Escola Bíblica' },
    { path: '/admin/programacao', icon: Baby, label: 'Programação' },
    { path: '/admin/contacts', icon: Mail, label: 'Contatos' },
    { path: '/admin/users', icon: Users, label: 'Usuários' },
    { path: '/admin/logs', icon: FileText, label: 'Logs' }
  ]

  const handleLogout = async () => {
    await lumi.auth.signOut()
    navigate('/')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <div className="bg-white shadow-sm sticky top-0 z-40">
        <div className="px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://static.lumi.new/material/fd/fd208cdb7c6b89103dc6a3e704c4cafe.png" 
                alt="Logo" 
                className="h-10"
              />
              <span className="font-semibold text-[#1e3a8a] hidden sm:block">Admin Panel</span>
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-900">{user?.userName || user?.email}</p>
              <p className="text-xs text-gray-500">Administrador</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-[#1e3a8a] transition-colors"
              title="Sair"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-16 left-0 h-[calc(100vh-4rem)] w-64 bg-white shadow-lg z-30 transform transition-transform
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <nav className="p-4 space-y-1 overflow-y-auto h-full">
            {menuItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-[#1e3a8a] text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              )
            })}
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/20 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  )
}

export default AdminLayout